import type { NormalisedHour } from '../daily-obs-types';
import type { SnowSceneSport } from './mountain-diorama';
import { SKI_LOCATIONS } from '../locations';
import { parseSurfHourlyScores } from './coastal-evidence';
import { surfForecastFreshness } from './surf-freshness';
import { resolveSpotLocalContext } from '../spot-locality';

export type MountainSceneLocation = {
  slug: string; name: string; country: string; region: string;
  latitude: number | null; longitude: number | null; timezone: string | null;
  elevationM: number | null;
};
export type MountainHourlyRating = { hour: number; score: number | null; isDay: boolean };
export type MountainDaySummary = {
  snowDepthCm: number | null; snowfallCm: number | null;
  tempMinC: number | null; tempMaxC: number | null; weatherCode: number | null;
};
export type MountainForecastDay = {
  date: string; hours: NormalisedHour[]; updatedAt: string | null;
  hourlyScores: Partial<Record<SnowSceneSport, MountainHourlyRating[]>>;
  summary?: MountainDaySummary | null;
};
export type MountainVisualiserForecast = {
  location: MountainSceneLocation; days: MountainForecastDay[];
};
export const SNOW_SCENE_SPORTS: { id: SnowSceneSport; label: string }[] = [
  { id: 'piste', label: 'Piste skiing' }, { id: 'off-piste', label: 'Off-piste' }, { id: 'touring', label: 'Ski touring' },
];
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export function mountainCatalogueLocation(slug: string): MountainSceneLocation {
  const row = SKI_LOCATIONS.find((location) => location.id === slug);
  return { slug, name: row?.name ?? slug, country: row?.country ?? '', region: row?.region ?? '',
    latitude: row?.lat ?? null, longitude: row?.lng ?? null, timezone: null, elevationM: row?.elevation ?? null };
}

/** Keep missing weather and snow missing. No interpolation or borrowing adjacent hours. */
export function parseMountainHours(value: unknown): NormalisedHour[] {
  if (!Array.isArray(value)) return [];
  const slots = new Map<number, NormalisedHour>();
  for (const entry of value.slice(0, 48)) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const row = entry as Partial<NormalisedHour>;
    if (!finite(row.hour) || !Number.isInteger(row.hour) || row.hour < 0 || row.hour > 23 || slots.has(row.hour)
      || !row.weather || typeof row.weather !== 'object' || Array.isArray(row.weather)) continue;
    // The renderer explicitly represents missing fields. Do not convert nulls to zero.
    slots.set(row.hour, row as NormalisedHour);
  }
  return [...slots.values()].sort((a, b) => a.hour - b.hour);
}

export function parseMountainSummary(value: unknown): MountainDaySummary | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const depth = finite(row.snowDepthCm) && row.snowDepthCm >= 0 ? row.snowDepthCm : null;
  const snowfall = finite(row.snowfallCm) && row.snowfallCm >= 0 ? row.snowfallCm : null;
  const tempMinC = finite(row.tempMinC) ? row.tempMinC : null;
  const tempMaxC = finite(row.tempMaxC) ? row.tempMaxC : null;
  if (depth === null && snowfall === null && tempMinC === null && tempMaxC === null) return null;
  return { snowDepthCm: depth, snowfallCm: snowfall, tempMinC, tempMaxC,
    weatherCode: finite(row.weatherCode) ? row.weatherCode : null };
}

export function mountainLocalClock(location: MountainSceneLocation, now: number): { date: string; minuteOfDay: number; approximate: boolean } {
  const clock = resolveSpotLocalContext(new Date(now), location);
  const minute = clock?.source === 'iana'
    ? Number(new Intl.DateTimeFormat('en-GB', { timeZone: clock.timeZone, minute: '2-digit' }).format(now))
    : new Date(now).getUTCMinutes();
  return { date: clock?.date ?? new Date(now).toISOString().slice(0, 10),
    minuteOfDay: (clock?.hour ?? new Date(now).getUTCHours()) * 60 + minute, approximate: clock?.source !== 'iana' };
}

/** Scores are accepted only for the exact selected daylight hour and a fresh issue. */
export function mountainRatingAt(day: MountainForecastDay | undefined, sport: SnowSceneSport, hour: NormalisedHour | undefined, now: number): number | null {
  if (!day || !hour || hour.weather.is_day !== true || surfForecastFreshness(day.updatedAt, now) !== 'fresh') return null;
  return parseSurfHourlyScores(day.hourlyScores[sport]).find((point) => point.hour === hour.hour && point.isDay)?.score ?? null;
}
